import { getTitle, getName, DATASETS } from '@/components/Constants';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SearchResultsProps {
  state: any;
  dispatch: any;
}

export type ScoreHistoryPanelState = {
  selectedYear: number | null;
  selectedYearFrom: number | null;
  selectedYearTo: number | null;
  yearInput: string;
  yearFromInput: string;
  yearToInput: string;
  tab?: string;
};

export type DetailGroup = {
  title: string;
  entries: Array<{ key: string; value: any; displayLabel: string }>;
};

// ─── Constants ────────────────────────────────────────────────────────────────

export const CATEGORY_ORDER = ['GDA', 'VDA', 'DDA', 'Gene', 'Disease', 'Variant', 'Chemical', 'Relation'];

export const ROW_DETAIL_CONTEXT_LABELS: Record<string, string> = {
  GDA: 'Gene - disease',
  VDA: 'Variant - disease',
  DDA: 'Disease - disease',
  DISEASES: 'Disease detail',
  GENES: 'Gene detail',
  VARIANTS: 'Variant detail',
  CHEMICALS: 'Chemical detail',
};

export const SHOW_HISTORIAL_SCORE = process.env.REACT_APP_SHOW_HISTORIAL_SCORE === 'true';
export const RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX = 52;

// ─── Search item helpers ───────────────────────────────────────────────────────

export function getSearchItemBadgeLabel(item: any): { label: string; tooltipId: string } {
  const title = getTitle(item);
  const id = getName(item);
  const titleStr =
    typeof title === 'object' && title !== null && 'title' in title
      ? String((title as { title?: string }).title ?? id)
      : String(title ?? '');
  const idStr = String(id ?? item?.id ?? '');
  const isAllDataset =
    typeof title === 'object' &&
    title !== null &&
    'title' in (title as object) &&
    (title as { title?: string }).title === DATASETS.ALL;
  const label = titleStr && !isAllDataset ? titleStr : idStr;
  return { label, tooltipId: idStr };
}

export function getNumericScoreFromSearchItem(item: any): number | null {
  const keys = ['score', 'matchScore', 'relevance', 'relevanceScore'] as const;
  for (const k of keys) {
    const v = item?.[k];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function sortSearchItemsForDisplay(items: any[]): any[] {
  if (!items?.length) return [];
  const decorated = items.map(item => ({ item, ...getSearchItemBadgeLabel(item) }));
  const anyScore = decorated.some(d => getNumericScoreFromSearchItem(d.item) != null);
  if (anyScore) {
    return [...decorated]
      .sort((a, b) => {
        const sa = getNumericScoreFromSearchItem(a.item);
        const sb = getNumericScoreFromSearchItem(b.item);
        if (sa != null && sb != null) return sb - sa;
        if (sa != null) return -1;
        if (sb != null) return 1;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      })
      .map(d => d.item);
  }
  return [...decorated]
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    .map(d => d.item);
}

export function getEntityPluralLabel(view: string | undefined, items: any[]): string {
  const byDataset: Record<string, string> = {
    DISEASES: 'diseases',
    GENES: 'genes',
    VARIANTS: 'variants',
    CHEMICALS: 'chemicals',
  };
  if (view && view !== 'ALL') return byDataset[view] ?? 'items';
  const datasets = new Set(items.map(i => i?.dataset).filter(Boolean) as string[]);
  if (datasets.size === 1) {
    const only = [...datasets][0];
    return byDataset[only] ?? 'items';
  }
  return 'items';
}

// ─── Value formatting helpers ─────────────────────────────────────────────────

export function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, c => c.toUpperCase());
}

export function normalizeValue(value: any, key?: string): string {
  if (value === null || value === undefined || value === '') return '-';
  if (key === 'chemsIncludedInEvidenceBySource') {
    if (Array.isArray(value)) return `${value.length} sources`;
    if (typeof value === 'object') return `${Object.keys(value).length} sources`;
  }
  if (Array.isArray(value)) {
    if (!value.length) return '-';
    const hasObjects = value.some(item => typeof item === 'object' && item !== null);
    if (hasObjects) return `${value.length} items`;
    return value.map(item => String(item)).join(', ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length) return '-';
    const preview = entries
      .slice(0, 4)
      .map(([entryKey, entryValue]) => `${humanizeKey(entryKey)}: ${normalizeValue(entryValue)}`)
      .join(' | ');
    return entries.length > 4 ? `${preview} | ...` : preview;
  }
  return String(value);
}

function isPliKey(key?: string): boolean {
  if (!key) return false;
  const normalizedKey = key.replace(/[_\s]/g, '').toLowerCase();
  return normalizedKey === 'pli' || normalizedKey === 'genepli';
}

function formatDetailNumericValue(value: number, key?: string): string {
  if (!Number.isFinite(value)) return String(value);
  if (isPliKey(key)) return Number.parseFloat(String(value)).toExponential(4);
  const roundedTwoDecimalPlaces = Math.round((value + Number.EPSILON) * 100) / 100;
  if (Number.isInteger(roundedTwoDecimalPlaces)) return String(roundedTwoDecimalPlaces);
  let text = roundedTwoDecimalPlaces.toFixed(2);
  text = text.replace(/(\.\d*?)0+$/, '$1');
  text = text.replace(/\.$/, '');
  return text;
}

export function normalizeDetailValue(value: any, key?: string): string {
  if (value === null || value === undefined || value === '') return '-';

  if (typeof value === 'number') {
    return formatDetailNumericValue(value, key);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const numericValue = Number(trimmed);
    if (trimmed !== '' && Number.isFinite(numericValue)) {
      return formatDetailNumericValue(numericValue, key);
    }
  }

  if (Array.isArray(value)) {
    if (!value.length) return '-';
    const hasObjects = value.some(item => typeof item === 'object' && item !== null);
    if (hasObjects) return `${value.length} items`;
    return value.map(item => normalizeDetailValue(item, key)).join(', ');
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length) return '-';
    const preview = entries
      .slice(0, 4)
      .map(
        ([entryKey, entryValue]) => `${humanizeKey(entryKey)}: ${normalizeDetailValue(entryValue, entryKey)}`,
      )
      .join(' | ');
    return entries.length > 4 ? `${preview} | ...` : preview;
  }

  return String(value);
}

export function countItems(value: any): number {
  if (value === null || value === undefined || value === '') return 0;
  if (Array.isArray(value)) return value.filter(Boolean).length;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.max(0, value) : 0;
  if (typeof value === 'string') {
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && value.trim() !== '') return Math.max(0, numericValue);
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean).length;
  }
  return 1;
}

// ─── Row detail helpers ────────────────────────────────────────────────────────

export function getRowValueByPriority(
  row: Record<string, any> | null | undefined,
  exactKeys: string[],
  includesKeys: string[] = [],
): any {
  if (!row || typeof row !== 'object') return undefined;
  for (const key of exactKeys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
  }
  if (!includesKeys.length) return undefined;
  const entries = Object.entries(row);
  for (const includeKey of includesKeys) {
    const found = entries.find(([k, v]) => {
      const valuePresent = v !== undefined && v !== null && v !== '';
      return valuePresent && k.toLowerCase().includes(includeKey.toLowerCase());
    });
    if (found) return found[1];
  }
  return undefined;
}

export function getRowDetailContextLabel(tab: string | undefined): string {
  if (tab == null || String(tab).trim() === '') return 'Association';
  const k = String(tab).toUpperCase();
  return ROW_DETAIL_CONTEXT_LABELS[k] ?? tab;
}

export function groupRowDetails(
  row: any,
  columnLabels: Record<string, string> | null | undefined = undefined,
  columnCategories: Record<string, string> | null | undefined = undefined,
  currentTab?: string,
): DetailGroup[] {
  if (!row || typeof row !== 'object') return [];
  const labelFor = (key: string) => columnLabels?.[key] ?? humanizeKey(key);
  const groups: Record<string, Array<{ key: string; value: any; displayLabel: string }>> = {};
  const ensureGroup = (cat: string) => {
    if (!groups[cat]) groups[cat] = [];
  };

  if (columnCategories && Object.keys(columnCategories).length > 0) {
    Object.entries(row).forEach(([key, value]) => {
      if (['id', 'dataset'].includes(key.toLowerCase())) return;
      if (value === null || value === undefined || value === '') return;
      const cat = columnCategories[key];
      if (!cat) return;
      ensureGroup(cat);
      groups[cat].push({ key, value, displayLabel: labelFor(key) });
    });
  } else {
    const keys = Object.keys(row).map(k => k.toLowerCase());
    const hasVariant = keys.some(k => k.includes('variant') || k.includes('dbsnp') || k.includes('hgv'));
    const diseaseLikeCount = keys.filter(k => k.includes('disease')).length;
    const inferredAssoc = hasVariant ? 'VDA' : diseaseLikeCount > 1 ? 'DDA' : 'GDA';

    Object.entries(row).forEach(([key, value]) => {
      const lower = key.toLowerCase();
      if (['id', 'dataset'].includes(lower)) return;
      if (value === null || value === undefined || value === '') return;
      const entry = { key, value, displayLabel: labelFor(key) };

      if (lower === 'coreaction' || lower.includes('relation')) {
        ensureGroup('Relation');
        groups.Relation.push(entry);
      } else if (lower.includes('gene')) {
        ensureGroup('Gene');
        groups.Gene.push(entry);
      } else if (lower.includes('disease')) {
        ensureGroup('Disease');
        groups.Disease.push(entry);
      } else if (lower.includes('chem')) {
        ensureGroup('Chemical');
        groups.Chemical.push(entry);
      } else if (
        lower.includes('variant') ||
        lower.includes('hgv') ||
        lower.includes('dbsnp') ||
        lower.includes('allele')
      ) {
        ensureGroup('Variant');
        groups.Variant.push(entry);
      } else {
        ensureGroup(inferredAssoc);
        groups[inferredAssoc].push(entry);
      }
    });
  }

  if ((currentTab || '').toUpperCase() !== 'CHEMICALS') {
    delete groups.Chemical;
  }

  const knownOrder = CATEGORY_ORDER.filter(cat => groups[cat]?.length);
  const extra = Object.keys(groups).filter(cat => !CATEGORY_ORDER.includes(cat) && groups[cat].length);
  return [...knownOrder, ...extra.sort()].map(title => ({ title, entries: groups[title] }));
}
